import { useEffect, useState } from 'react'

/**
 * Create-watchlist modal — 1:1 port of the desktop
 * `src/renderer/components/watchlist/CreateWatchlistModal.tsx`:
 * name (required) + optional description + color swatches, Escape to
 * close, body scroll lock, autoFocus on the name field, inline error
 * display, and a disabled submit button until the name is non-empty.
 *
 * Validation (unique name across existing watchlists) is enforced by
 * the caller since this component is purely presentational.
 */
interface Props {
  isOpen: boolean
  onClose: () => void
  onCreateWatchlist: (data: {
    name: string
    description?: string
    color?: string
  }) => Promise<void>
}

const COLOR_OPTIONS = [
  '#3b82f6', // Blue
  '#ef4444', // Red
  '#10b981', // Green
  '#f59e0b', // Yellow
  '#8b5cf6', // Purple
  '#f97316', // Orange
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#ec4899', // Pink
  '#6b7280', // Gray
]

export function CreateWatchlistModal({ isOpen, onClose, onCreateWatchlist }: Props) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState<string>('#3b82f6')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset the form every time the modal is re-opened.
  useEffect(() => {
    if (isOpen) {
      setName('')
      setDescription('')
      setColor('#3b82f6')
      setError(null)
      setIsLoading(false)
    }
  }, [isOpen])

  // Escape to close + body scroll lock while open.
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Watchlist name is required')
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      await onCreateWatchlist({
        name: trimmed,
        description: description.trim() || undefined,
        color,
      })
      onClose()
    } catch (err) {
      console.error('[CreateWatchlistModal] create failed', err)
      setError((err as Error).message || 'Failed to create watchlist')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border-primary bg-bg-primary shadow-cinematic-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-primary p-4">
          <h2 className="text-base font-bold text-text-primary">Create new watchlist</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md bg-bg-secondary px-3 py-1 text-xs text-text-secondary hover:bg-bg-tertiary"
          >
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-400">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="pwa-watchlist-name"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider text-text-tertiary"
            >
              Name *
            </label>
            <input
              id="pwa-watchlist-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekend picks"
              disabled={isLoading}
              autoFocus
              className="w-full rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-primary/50 focus:outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="pwa-watchlist-description"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider text-text-tertiary"
            >
              Description
            </label>
            <textarea
              id="pwa-watchlist-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              rows={3}
              disabled={isLoading}
              className="w-full resize-none rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-primary/50 focus:outline-none"
            />
          </div>

          <div>
            <p className="mb-2 block text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              Color
            </p>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  disabled={isLoading}
                  aria-label={`Select color ${c}`}
                  className={`h-8 w-8 rounded-full border-2 transition-transform duration-150 ${
                    color === c
                      ? 'scale-110 border-white shadow-lg'
                      : 'border-border-primary hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-md px-4 py-2 text-sm text-text-secondary hover:text-text-primary disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className="flex items-center gap-2 rounded-md bg-accent-primary px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading && (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-black/30 border-t-black" />
              )}
              {isLoading ? 'Creating…' : 'Create watchlist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
